/**
 * Pure grid math helpers — kept separate from canvas.ts so they can be
 * unit-tested without a DOM/canvas.
 *
 * All "logical" coordinates are in image pixels (the original image space
 * before viewport zoom/pan). The viewport transform `ctx.setTransform(zoom,0,0,zoom,panX,panY)`
 * maps logical → screen. We invert that for screen → logical.
 */

import type { MapRecord, Viewport } from './types.js';

/** Pixels per grid cell, where one cell == 5 ft. */
export function cellPx(map: Pick<MapRecord, 'image_path' | 'width_ft'>, imagePixelWidth: number): number {
  // 5 ft per cell is the D&D standard the spec assumes.
  const cellsAcross = map.width_ft / 5;
  if (cellsAcross <= 0) {
    throw new Error('Map width_ft must be > 0');
  }
  return imagePixelWidth / cellsAcross;
}

/** Convert a screen-space pixel into a logical (image-space) pixel. */
export function screenToLogical(
  screenX: number,
  screenY: number,
  viewport: Viewport,
): { x: number; y: number } {
  return {
    x: (screenX - viewport.x) / viewport.zoom,
    y: (screenY - viewport.y) / viewport.zoom,
  };
}

/** Convert a screen-space pixel into a grid cell (floored). */
export function screenToGrid(
  screenX: number,
  screenY: number,
  cellPxValue: number,
  viewport: Viewport,
): { grid_x: number; grid_y: number } {
  if (cellPxValue <= 0) {
    throw new Error('cellPx must be > 0');
  }
  const { x, y } = screenToLogical(screenX, screenY, viewport);
  return {
    grid_x: Math.floor(x / cellPxValue),
    grid_y: Math.floor(y / cellPxValue),
  };
}

/** Center of a grid cell in logical coordinates. */
export function gridCellCenter(gridX: number, gridY: number, cellPxValue: number): { cx: number; cy: number } {
  return {
    cx: (gridX + 0.5) * cellPxValue,
    cy: (gridY + 0.5) * cellPxValue,
  };
}
